import { cn } from '@/lib/utils';
import { CardHeader } from '../../../components/ui/card';
import { Card } from '../../../components/ui/card';
import { TrendingDown, TrendingUp, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface DashboardSmallCardProps {
  title: string;
  value: string;
  growth?: string;
  growthTrend?: 'up' | 'down';
  selected?: boolean;
  onClick?: () => void;
  isLoading?: boolean;
  tooltip?: string;
}

export function DashboardSmallCard({
  title,
  value,
  growth,
  growthTrend,
  selected,
  onClick,
  isLoading,
  tooltip
}: DashboardSmallCardProps) {
  const getStatusColor = (type: 'bg' | 'text') => {
    if (!growthTrend) {
      return type === 'bg' ? 'bg-muted-foreground' : 'text-muted-foreground';
    }
    switch (growthTrend) {
      case 'up':
        return type === 'bg' ? 'bg-success-foreground' : 'text-success-foreground';
      case 'down':
        return type === 'bg' ? 'bg-destructive-foreground' : 'text-destructive-foreground';
      default:
        return type === 'bg' ? 'bg-muted-foreground' : 'text-muted-foreground';
    }
  };

  return (
    <Card
      className={cn(
        'rounded-t-lg rounded-b-none border-0 border-b border-dashed bg-card hover:bg-card-accent',
        selected ? 'bg-midground border-border-medium hover:bg-card-accent' : 'bg-transparent',
        onClick ? 'cursor-pointer hover:bg-card-accent transition-colors' : ''
      )}
      onClick={onClick}
    >
      <CardHeader className="pt-4 pr-6 pb-6 pl-6 flex justify-between font-mono">
        <div className="flex flex-row items-start gap-1 text-sm text-card-foreground min-h-[2.5rem]">
          <span className="tracking-[0.04em] text-muted-foreground leading-tight">{title}</span>
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-pointer flex-shrink-0 mt-0.5" />
                </TooltipTrigger>
                <TooltipContent className="font-sans max-w-[300px]">
                  <p>{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {isLoading ? (
          <div className="h-6 w-24 animate-pulse rounded bg-muted" />
        ) : (
          <div className="flex justify-baseline text-2xl font-sans font-semibold text-foreground">
            {value}
          </div>
        )}
        {growth && growthTrend && (
          <div className={cn('flex justify-baseline text-[12px] gap-1', getStatusColor('text'))}>
            {growthTrend === 'up' ? (
              <TrendingUp className="h-4 w-4 text-success-foreground" />
            ) : (
              <TrendingDown className="h-4 w-4 text-destructive-foreground" />
            )}
            {growth}
          </div>
        )}
      </CardHeader>
    </Card>
  );
}
